import React from 'react';
import {useNavigate, useParams} from "react-router-dom";
import {useMutation, useQuery} from "react-query";
import ngage from "services/ngage";
import {useNotifications} from "context/NotificationContext";
import {FormikHelpers} from "formik";
import {invalidate} from "services/query-client";
import Page from "components/Page";
import {GreetingData} from "types/Greeting";
import GreetingForm from '../Form';

function MutateGreeting() {
    const {id} = useParams()
    const {data: greeting, isLoading} = useQuery(
        ['greetings', {id}],
        () => ngage.greetings.get(parseInt(id as string)),
        {enabled: !!id}
    )

    const store = useMutation((data: GreetingData) => ngage.greetings.store(data))
    const update = useMutation((data: GreetingData) => ngage.greetings.update(parseInt(id as string), data))

    const {notify} = useNotifications()
    const navigate = useNavigate()

    // Handle form submission
    async function handleSubmit(values: GreetingData, {setSubmitting}: FormikHelpers<GreetingData>) {
        id? await handleUpdate(values) : await handleStore(values)
        await invalidate('greetings')

        setSubmitting(false)
        navigate('/greetings')
    }

    // Handle store mutation
    async function handleStore(values: GreetingData) {
        try {
            await store.mutateAsync(values)
            notify("success", "Greeting Created", `${values.title} has been created`)
        } catch (error) {
            notify("error", "Greeting Not Created", `${values.title} could not be stored`)
            throw error
        }
    }

    // Handle update mutation
    async function handleUpdate(values: GreetingData) {
        try {
            await update.mutateAsync(values)
            notify("success", "Greeting Updated", `${values.title} has been updated`)
            await invalidate(['greetings', {id}])
        } catch (error) {
            notify("error", "Greeting Not Updated", `${values.title} could not be updated`)
            throw error
        }
    }

    // Prepare breadcrumbs
    const newBreadcrumbs = {'App Content': '/', 'Featured': '/', 'Greetings': '/greetings', 'New Greeting': '/greetings/new'}
    const editBreadcrumbs = {'App Content': '/', 'Featured': '/', 'Greetings': '/greetings', 'Edit Greeting': `/greetings/${id}/edit`}

    return (
        <Page title={id ? "Edit Greeting" : "New Greeting"}
              breadcrumbs={id ? editBreadcrumbs : newBreadcrumbs}
              isLoading={isLoading}>
            <GreetingForm greeting={greeting} onSubmit={handleSubmit}/>
        </Page>
    );
}

export default MutateGreeting;